import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { FrameAnswer } from "@/lib/api/assistantV2";
import { formatCell, isNumericCell } from "../utils";
import { AnswerNotes } from "./AnswerNotes";

/** Renders a frame answer (columns + rows) as a scrollable table. */
export function FrameAnswerCard({ answer }: { answer: FrameAnswer }) {
  const { columns, rows } = answer;
  // Per-column tooltip text (agent glossary + plan overrides); may be absent.
  const colDescriptions = answer.column_descriptions ?? {};

  // A column is numeric if every non-null cell in it is a finite number.
  const numericCols = columns.map((_, c) =>
    rows.every((row) => row[c] == null || isNumericCell(row[c])),
  );

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-baseline justify-between gap-3 flex-wrap">
        <div className="text-sm font-semibold text-slate-900">
          {answer.title ?? "Frame"}
        </div>
        <div className="flex items-center gap-2">
          {answer.study_id && (
            <Badge
              variant="outline"
              className="border-violet-200 text-violet-700 bg-white text-[10px] px-1.5 font-mono"
            >
              {answer.study_id}
              {answer.version ? `@${answer.version}` : ""}
            </Badge>
          )}
          <div className="text-xs text-slate-500 tabular-nums">
            {rows.length} {rows.length === 1 ? "row" : "rows"}
          </div>
        </div>
      </div>

      {columns.length === 0 || rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-slate-500 text-center">
          Empty frame.
        </div>
      ) : (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="text-sm w-full border-collapse">
            <thead className="sticky top-0 bg-slate-50">
              <tr className="text-slate-600 text-left">
                <TooltipProvider delayDuration={150}>
                  {columns.map((col, c) => {
                    const desc = colDescriptions[col];
                    return (
                      <th
                        key={c}
                        className={`px-3 py-2 font-medium whitespace-nowrap ${
                          numericCols[c] ? "text-right" : ""
                        }`}
                      >
                        {desc ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2">
                                {col}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              {desc}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          col
                        )}
                      </th>
                    );
                  })}
                </TooltipProvider>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r} className="border-t border-slate-200">
                  {columns.map((_, c) => (
                    <td
                      key={c}
                      className={`px-3 py-2 whitespace-nowrap ${
                        numericCols[c] ? "tabular-nums text-right" : ""
                      } ${row[c] == null ? "text-slate-400" : "text-slate-800"}`}
                    >
                      {formatCell(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnswerNotes
        notes={answer.notes}
        warnings={answer.warnings}
        diagnostics={answer.diagnostics}
      />
    </div>
  );
}
